import { create } from 'zustand';
import { message } from 'antd';
import apiCore from '../utils/api-core';
import {
    getBots,
    createBot as apiCreateBot,
    copyBot as apiCopyBot,
    assignBotToUser,
    setStartModule as apiSetStartModule,
    generateBotQR,
} from '../reducers/bots/resources';
import { getUsersByCompanyId, getUsers } from '../reducers/user/resources';
import { getAllCompanies } from '../reducers/company/resources';
import { getDepartmentByCompany } from '../reducers/department/resources';
import { getEmailGroupsByCompany } from '../reducers/group/resources';
import { getVonageNumbersAvailableList } from '../reducers/nexmo/resources';

const useBotBuilderStore = create((set, get) => ({
    loading: false,
    context: 'bot',

    // Selected bot + its modules
    bot: null,
    modules: [],
    selectedModuleId: null,
    botUsers: [],

    // Reference data
    botList: [],
    companyList: [],
    users: [],
    emailGroups: [],
    companyDepartments: [],
    availableNumbers: [],

    // ── Reference data loaders ──────────────────────────
    fetchBotList: async () => {
        try {
            const res = await getBots();
            set({ botList: res.data || [] });
        } catch (error) {
            console.error(error);
            message.error('Failed to load bots');
        }
    },

    fetchCompanies: async () => {
        try {
            const res = await getAllCompanies();
            set({ companyList: res.data || [] });
        } catch (error) {
            console.error(error);
        }
    },

    fetchUsers: async () => {
        try {
            const res = await getUsers();
            set({ users: res.data || [] });
        } catch (error) {
            console.error(error);
        }
    },

    fetchUsersForCompany: async (companyId) => {
        if (!companyId) {
            set({ users: [] });
            return;
        }
        try {
            const res = await getUsersByCompanyId(companyId);
            set({ users: res.data || [] });
        } catch (error) {
            console.error(error);
            set({ users: [] });
        }
    },

    fetchVonageNumbers: async () => {
        try {
            const res = await getVonageNumbersAvailableList();
            set({ availableNumbers: res.data || [] });
        } catch (error) {
            console.error(error);
        }
    },

    // ── Bot-scoped fetches ──────────────────────────────
    fetchBot: async (botId) => {
        if (!botId) {
            set({ bot: null, modules: [], selectedModuleId: null, emailGroups: [], companyDepartments: [], botUsers: [], users: [] });
            return;
        }
        set({ loading: true });
        try {
            const res = await apiCore.get(`/api/v3/bots/${botId}`);
            const data = res.data || res;
            set({
                bot: data,
                modules: data.modules || [],
                loading: false,
            });
            if (data.company_id) {
                get().fetchEmailGroups(data.company_id);
                get().fetchCompanyDepartments(data.company_id);
                get().fetchUsersForCompany(data.company_id);
            }
            get().fetchBotUsers(botId);
        } catch (error) {
            console.error(error);
            set({ loading: false });
            message.error('Failed to load bot');
        }
    },

    fetchBotUsers: async (botId) => {
        if (!botId) {
            set({ botUsers: [] });
            return;
        }
        try {
            const res = await apiCore.get(`/api/v3/bots/${botId}/users`);
            set({ botUsers: res.data || res || [] });
        } catch (error) {
            console.error(error);
            set({ botUsers: [] });
        }
    },

    fetchEmailGroups: async (companyId) => {
        try {
            const res = await getEmailGroupsByCompany(companyId);
            set({ emailGroups: res.data || [] });
        } catch (error) {
            console.error(error);
        }
    },

    fetchCompanyDepartments: async (companyId) => {
        try {
            const res = await getDepartmentByCompany(companyId);
            set({ companyDepartments: res.data || [] });
        } catch (error) {
            console.error(error);
        }
    },

    // ── Bot lifecycle ───────────────────────────────────
    createBot: async (data) => {
        try {
            const res = await apiCreateBot(data);
            message.success('Bot created');
            await get().fetchBotList();
            return res.data;
        } catch (error) {
            console.error(error);
            message.error(error?.message || 'Failed to create bot');
            throw error;
        }
    },

    copyBot: async (sourceId, destId) => {
        try {
            await apiCopyBot(sourceId, destId);
            message.success('Bot copied');
            await get().fetchBotList();
            if (get().bot && get().bot.id === destId) {
                await get().fetchBot(destId);
            }
        } catch (error) {
            console.error(error);
            message.error(error?.message || 'Failed to copy bot');
        }
    },

    updateBot: async (data) => {
        const { bot } = get();
        if (!bot) return;
        try {
            const res = await apiCore.put(`/api/v3/bots/${bot.id}`, data);
            const updated = res.data || res;
            set((state) => ({ bot: { ...state.bot, ...updated } }));
            message.success('Bot updated');
        } catch (error) {
            console.error(error);
            message.error(error?.message || 'Failed to update bot');
        }
    },

    saveFlowMeta: async (flowMetaData) => {
        const { bot } = get();
        if (!bot) return;
        try {
            await apiCore.put(`/api/v3/bots/${bot.id}`, { flow_meta: flowMetaData });
            set((state) => ({ bot: { ...state.bot, flow_meta: flowMetaData } }));
            message.success('Canvas layout saved');
        } catch (error) {
            console.error(error);
            message.error('Failed to save layout');
        }
    },

    setStartModule: async (moduleId) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiSetStartModule(bot.id, moduleId);
            message.success('Start module updated');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to set start module');
        }
    },

    saveOverrides: async (overrides) => {
        const { bot } = get();
        if (!bot) return;
        try {
            await apiCore.put(`/api/v3/bots/${bot.id}`, overrides);
            set((state) => ({ bot: { ...state.bot, ...overrides } }));
            message.success('Overrides saved');
        } catch (error) {
            console.error(error);
            message.error('Failed to save overrides');
        }
    },

    assignUser: async (userId) => {
        const { bot, fetchBotUsers } = get();
        if (!bot) return;
        try {
            await assignBotToUser(bot.id, userId);
            message.success('User assigned');
            await fetchBotUsers(bot.id);
        } catch (error) {
            console.error(error);
            message.error(error?.message || 'Failed to assign user');
        }
    },

    unassignUser: async (userId) => {
        const { bot, fetchBotUsers } = get();
        if (!bot) return;
        try {
            await apiCore.delete(`/api/v3/bots/${bot.id}/users/${userId}`);
            message.success('User unassigned');
            await fetchBotUsers(bot.id);
        } catch (error) {
            console.error(error);
            message.error(error?.message || 'Failed to unassign user');
        }
    },

    generateQR: async (scanText) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await generateBotQR({ botId: bot.id, scanText, botDetails: bot });
            message.success('QR generated');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to generate QR');
        }
    },

    // ── Modules (v3 neutral endpoints) ──────────────────
    createModule: async (moduleData) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.post('/api/v3/modules', { ...moduleData, bot_id: bot.id });
            message.success('Module created');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create module');
        }
    },

    updateModule: async (moduleId, moduleData) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.put(`/api/v3/modules/${moduleId}`, { ...moduleData, bot_id: bot.id });
            message.success('Module updated');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update module');
        }
    },

    deleteModule: async (moduleId) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.delete(`/api/v3/modules/${moduleId}`);
            message.success('Module deleted');
            set({ selectedModuleId: null });
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            // Surface the backend's explanation for client-side rejections
            // (e.g. 409 when the module is still in use by conversations);
            // 5xx payloads carry only opaque text, so keep the generic fallback.
            const clientMessage = error?.statusCode >= 400 && error?.statusCode < 500
                ? error.message
                : null;
            message.error(clientMessage || 'Failed to delete module');
        }
    },

    // ── Connections (v3 neutral endpoints) ──────────────
    createConnection: async (moduleId, goto, filters) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.post('/api/v3/connections', { module_id: moduleId, goto, filters });
            message.success('Connection created');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create connection');
        }
    },

    deleteConnection: async (moduleId, gotoId) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.delete(`/api/v3/connections/${moduleId}/${gotoId}`);
            message.success('Connection deleted');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to delete connection');
        }
    },

    // ── Triggers (shared v3 endpoints) ──────────────────
    createTrigger: async (moduleId, gotoModuleId, triggerType, config) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.post('/api/v3/triggers', {
                module_id: moduleId,
                goto_module_id: gotoModuleId,
                trigger_type: triggerType,
                config,
            });
            message.success('Trigger created');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create trigger');
        }
    },

    updateTrigger: async (triggerId, data) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.put(`/api/v3/triggers/${triggerId}`, data);
            message.success('Trigger updated');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update trigger');
        }
    },

    deleteTrigger: async (triggerId) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.delete(`/api/v3/triggers/${triggerId}`);
            message.success('Trigger deleted');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to delete trigger');
        }
    },

    // ── Bot-only module extras (jsonb blobs) ────────────
    // v3 update-module requires title+response, so we pull the rest of the
    // module from local state and just override the jsonb field being patched.
    _patchModuleJsonb: async (moduleId, extraFields, successMessage, errorMessage) => {
        const { bot, modules, fetchBot } = get();
        if (!bot) return;
        const mod = modules.find((m) => m.id === moduleId);
        if (!mod) {
            message.error('Module not found');
            return;
        }
        try {
            await apiCore.put(`/api/v3/modules/${moduleId}`, {
                bot_id: bot.id,
                title: mod.title,
                reply_type: mod.reply_type || '',
                response: mod.response,
                options: mod.options,
                quick_replies: mod.quick_replies,
                error_response: mod.error_response || '',
                fallback: mod.fallback || null,
                field: mod.field || '',
                email_groups: JSON.stringify(mod.email_groups || []),
                ...extraFields,
            });
            message.success(successMessage);
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error(errorMessage);
        }
    },

    updatePathForks: async (moduleId, pathForks) => {
        await get()._patchModuleJsonb(
            moduleId,
            { path_forks: JSON.stringify(pathForks) },
            'Forks updated',
            'Failed to update forks',
        );
    },

    updateBotRedirect: async (moduleId, botRedirect) => {
        await get()._patchModuleJsonb(
            moduleId,
            { bot_redirect: JSON.stringify(botRedirect) },
            'Bot redirects updated',
            'Failed to update bot redirects',
        );
    },

    updateFollowups: async (moduleId, followups) => {
        await get()._patchModuleJsonb(
            moduleId,
            { followups: JSON.stringify(followups) },
            'Followups updated',
            'Failed to update followups',
        );
    },

    // ── Tags (v3 neutral endpoints) ─────────────────────
    addTag: async (moduleId, type, from, to) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.post('/api/v3/modules/tags', { module_id: moduleId, type, from, to });
            message.success('Tag added');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to add tag');
        }
    },

    updateTag: async (tagData) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        const { id, type, value } = tagData;
        try {
            await apiCore.put('/api/v3/modules/tags', { id, type, from: value?.from, to: value?.to });
            message.success('Tag updated');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update tag');
        }
    },

    deleteTag: async (tagId) => {
        const { bot, fetchBot } = get();
        if (!bot) return;
        try {
            await apiCore.delete(`/api/v3/modules/tags/${tagId}`);
            message.success('Tag removed');
            await fetchBot(bot.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to remove tag');
        }
    },

    // ── Selection ───────────────────────────────────────
    setSelectedModuleId: (id) => set({ selectedModuleId: id }),

    getSelectedModule: () => {
        const { modules, selectedModuleId } = get();
        if (!selectedModuleId) return null;
        return modules.find((m) => m.id === selectedModuleId) || null;
    },
}));

export default useBotBuilderStore;
